import {
    useContext,
    useEffect,
    useMemo
} from 'react';

import type {
    Dispatch,
    MouseEvent,
    SetStateAction
} from 'react';

import {
    Link,
    useLocation
} from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import dashboardIcon from '../../assets/images/dashboard-icon.svg';
import locationsIcon from '../../assets/images/locations-icon.svg';
import terminalsIcon from '../../assets/images/terminals-icon.svg';
import devicesIcon from '../../assets/images/devices-icon.svg';
import planogramsIcon from '../../assets/images/planograms-icon.svg';
import productsIcon from '../../assets/images/products-icon.svg';
import cashOperationIcon from '../../assets/images/cash-operations-icon.svg';
import routesIcon from '../../assets/images/routes-icon.svg';
import reportsIcon from '../../assets/images/reports-icon.svg';
import usersIcon from '../../assets/images/users-icon.svg';
import eventsIcon from '../../assets/images/events-icon.svg';
import transactionsIcon from '../../assets/images/transactions-icon.svg';
import operatorsIcon from '../../assets/images/operators-icon.svg';
import merchantsIcon from '../../assets/images/merchants-icon.svg';
import softwareUpdatesIcon from '../../assets/images/software-updates-icon.svg';

import { LayoutContext } from '../../context/LayoutContext';

import {
    AccessLevel,
    useAuth
} from '../../hook/useAuth';

import { useAppVersion } from '../../context/AppVersionContext';

import { getRuntimeConfig } from '../../utils/runtimeConfig';

type MenuMode = 'static' | 'slim';

type AccessLevelValue =
    (typeof AccessLevel)[keyof typeof AccessLevel];

interface MenuProps {
    mode: MenuMode;
}

interface MenuItem {
    label: string;
    icon: string;
    link: string;
    permissions: string[];
    accessLevels: AccessLevelValue[];
}

interface LayoutContextValue {
    setPageTitle: Dispatch<
        SetStateAction<string | undefined>
    >;

    setShowPageTitle: Dispatch<
        SetStateAction<boolean>
    >;

    setShowBackButton: Dispatch<
        SetStateAction<boolean>
    >;

    setBackButtonLabel: Dispatch<
        SetStateAction<string | undefined>
    >;

    switchMenuHandleOnClick:
        | ((destination: string) => boolean | void)
        | null;
}

interface AppVersionInfo {
    version?: string | null;
    buildNumber?: string | number | null;
}

interface AppVersionContextValue {
    appVersionInfo?: AppVersionInfo | null;
}

const Menu = ({ mode }: MenuProps) => {
    const location = useLocation();
    const { t } = useTranslation();

    const {
        setPageTitle,
        setShowPageTitle,
        setShowBackButton,
        setBackButtonLabel,
        switchMenuHandleOnClick
    } = useContext(LayoutContext) as LayoutContextValue;

    const {
        isUserInAnyRole,
        getAccessLevel
    } = useAuth();

    const {
        appVersionInfo
    } = useAppVersion() as AppVersionContextValue;

    const accessLevel = useMemo<AccessLevelValue>(
        () => getAccessLevel(),
        [getAccessLevel]
    );

    const isRoutesDisabled =
        getRuntimeConfig('REACT_APP_ROUTES_DISABLED') === 'true';

    const isCashOperationsDisabled =
        getRuntimeConfig('REACT_APP_CASH_OPERATIONS_DISABLED') === 'true';

    const items = useMemo<MenuItem[]>(() => {
        const menuItems: MenuItem[] = [
            {
                label: String(t('menu.dashboard')),
                icon: dashboardIcon,
                link: '/dashboard',
                permissions: [
                    'DASHBOARD_READ',
                    'DASHBOARD_WRITE'
                ],
                accessLevels: [
                    AccessLevel.MERCHANT
                ]
            },
            {
                label: String(t('menu.locations')),
                icon: locationsIcon,
                link: '/locations',
                permissions: [
                    'LOCATIONS_READ',
                    'LOCATIONS_WRITE'
                ],
                accessLevels: [
                    AccessLevel.MERCHANT
                ]
            },
            {
                label: String(t('menu.terminals')),
                icon: terminalsIcon,
                link: '/terminals',
                permissions: [
                    'TERMINALS_READ',
                    'TERMINALS_WRITE'
                ],
                accessLevels: [
                    AccessLevel.OPERATOR,
                    AccessLevel.ADMIN,
                    AccessLevel.MERCHANT
                ]
            },
            {
                label: String(t('menu.devices')),
                icon: devicesIcon,
                link: '/devices',
                permissions: [
                    'DEVICES_READ',
                    'DEVICES_WRITE'
                ],
                accessLevels: [
                    AccessLevel.MERCHANT
                ]
            },
            {
                label: String(t('menu.planograms')),
                icon: planogramsIcon,
                link: '/planograms',
                permissions: [
                    'PLANOGRAMS_READ',
                    'PLANOGRAMS_WRITE'
                ],
                accessLevels: [
                    AccessLevel.MERCHANT
                ]
            },
            {
                label: String(t('menu.products')),
                icon: productsIcon,
                link: '/products',
                permissions: [
                    'PRODUCTS_READ',
                    'PRODUCTS_WRITE'
                ],
                accessLevels: [
                    AccessLevel.MERCHANT,
                    AccessLevel.ADMIN
                ]
            },
            {
                label: String(t('menu.cash-operations')),
                icon: cashOperationIcon,
                link: '/cash-operations',
                permissions: [
                    'CASH_OPERATIONS_READ',
                    'CASH_OPERATIONS_WRITE'
                ],
                accessLevels: [
                    AccessLevel.MERCHANT
                ]
            },
            {
                label: String(t('menu.routes')),
                icon: routesIcon,
                link: '/routes',
                permissions: [
                    'ROUTES_READ',
                    'ROUTES_WRITE'
                ],
                accessLevels: [
                    AccessLevel.MERCHANT
                ]
            },
            {
                label: String(t('menu.reports')),
                icon: reportsIcon,
                link: '/reports',
                permissions: [
                    'REPORTS_READ',
                    'REPORTS_WRITE'
                ],
                accessLevels: [
                    AccessLevel.MERCHANT,
                    AccessLevel.ADMIN
                ]
            },
            {
                label: String(t('menu.users')),
                icon: usersIcon,
                link: '/users',
                permissions: [
                    'USERS_READ',
                    'USERS_WRITE'
                ],
                accessLevels: [
                    AccessLevel.ADMIN,
                    AccessLevel.OPERATOR,
                    AccessLevel.MERCHANT
                ]
            },
            {
                label: String(t('menu.events')),
                icon: eventsIcon,
                link: '/events',
                permissions: [
                    'EVENTS_READ',
                    'EVENTS_WRITE'
                ],
                accessLevels: [
                    AccessLevel.MERCHANT
                ]
            },
            {
                label: String(t('menu.transactions')),
                icon: transactionsIcon,
                link: '/transactions',
                permissions: [
                    'TRANSACTIONS_READ',
                    'TRANSACTIONS_WRITE'
                ],
                accessLevels: [
                    AccessLevel.MERCHANT,
                    AccessLevel.OPERATOR,
                    AccessLevel.ADMIN
                ]
            },
            {
                label: String(t('menu.operators')),
                icon: operatorsIcon,
                link: '/operators',
                permissions: [
                    'OPERATORS_READ',
                    'OPERATORS_WRITE'
                ],
                accessLevels: [
                    AccessLevel.ADMIN
                ]
            },
            {
                label: String(t('menu.merchants')),
                icon: merchantsIcon,
                link: '/merchants',
                permissions: [
                    'MERCHANTS_READ',
                    'MERCHANTS_WRITE'
                ],
                accessLevels: [
                    AccessLevel.ADMIN,
                    AccessLevel.OPERATOR
                ]
            },
            {
                label: String(t('menu.software-updates')),
                icon: softwareUpdatesIcon,
                link: '/software-updates',
                permissions: [
                    'SOFTWARE_UPDATES_READ',
                    'SOFTWARE_UPDATES_WRITE'
                ],
                accessLevels: [
                    AccessLevel.ADMIN
                ]
            }
        ];

        return menuItems
            .filter((item) => (
                isRoutesDisabled
                    ? item.link !== '/routes'
                    : true
            ))
            .filter((item) => (
                isCashOperationsDisabled
                    ? item.link !== '/cash-operations'
                    : true
            ));
    }, [
        t,
        isRoutesDisabled,
        isCashOperationsDisabled
    ]);

    const visibleItems = useMemo<MenuItem[]>(
        () => items.filter((item) => {
            if (
                item.accessLevels.length > 0 &&
                !item.accessLevels.includes(accessLevel)
            ) {
                return false;
            }

            if (
                item.permissions.length > 0 &&
                !isUserInAnyRole(item.permissions)
            ) {
                return false;
            }

            return true;
        }),
        [
            items,
            accessLevel,
            isUserInAnyRole
        ]
    );

    const handleMenuItemClick = (
        event: MouseEvent<HTMLAnchorElement>,
        item: MenuItem
    ): void => {
        /*
         * Preserve native link behavior for modified clicks:
         *
         * - Ctrl + click
         * - Cmd + click
         * - Shift + click
         * - Alt + click
         * - middle mouse click
         * - browser context menu
         */
        if (
            event.button !== 0 ||
            event.ctrlKey ||
            event.metaKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return;
        }

        /*
         * Avoid navigating to the same page again after a normal
         * left-click.
         */
        if (location.pathname === item.link) {
            event.preventDefault();
            return;
        }

        /*
         * Preserve the existing page-change guard.
         */
        if (typeof switchMenuHandleOnClick === 'function') {
            try {
                const shouldContinue =
                    switchMenuHandleOnClick(item.link);

                if (shouldContinue === false) {
                    event.preventDefault();
                }
            } catch (error: unknown) {
                event.preventDefault();

                console.error(
                    'Menu: Error during switchMenuHandleOnClick:',
                    error
                );
            }
        }

        /*
         * React Router's Link performs the navigation automatically
         * unless event.preventDefault() was called.
         */
    };

    const getCurrentPathname = (): string => {
        if (!location.pathname || location.pathname === '/') {
            return accessLevel === AccessLevel.MERCHANT
                ? '/dashboard'
                : '/terminals';
        }

        return location.pathname;
    };

    const isItemActive = (
        item: MenuItem
    ): boolean => {
        const pathname = getCurrentPathname();

        return (
            pathname === item.link ||
            pathname.startsWith(`${item.link}/`)
        );
    };

    const isItemActiveExactly = (
        item: MenuItem
    ): boolean => {
        return getCurrentPathname() === item.link;
    };

    const activeItemExactly = visibleItems.find(
        (item) => isItemActiveExactly(item)
    );

    const versionLabel = appVersionInfo?.version
        ? `${appVersionInfo.version}${
            appVersionInfo.buildNumber != null
                ? ` (${appVersionInfo.buildNumber})`
                : ''
        }`
        : null;

    useEffect(() => {
        setShowPageTitle(true);

        if (activeItemExactly) {
            setPageTitle(activeItemExactly.label);
            setShowBackButton(false);
            setBackButtonLabel(undefined);
        }
    }, [
        activeItemExactly,
        setPageTitle,
        setShowPageTitle,
        setShowBackButton,
        setBackButtonLabel
    ]);

    return (
        <>
            {visibleItems.map((item) => (
                <div
                    className={`main-menu mode-${mode}`}
                    key={item.link}
                >
                    <Link
                        to={item.link}
                        className={
                            `main-menu-item-wrapper ${
                                isItemActive(item)
                                    ? 'active'
                                    : ''
                            }`
                        }
                        onClick={(event) => {
                            handleMenuItemClick(event, item);
                        }}
                    >
                        <div className="main-menu-item">
                            <div className="main-menu-item-icon">
                                <img
                                    src={item.icon}
                                    className="image"
                                    alt=""
                                />
                            </div>

                            <div className="main-menu-item-label">
                                {item.label}
                            </div>

                            <div className="active-marker" />
                        </div>
                    </Link>
                </div>
            ))}

            {versionLabel && (
                <div
                    className={`main-menu mode-${mode} version`}
                >
                    <div className="main-menu-item-wrapper cursor-auto">
                        <div className="main-menu-item cursor-auto">
                            <div className="main-menu-item-version">
                                {versionLabel}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Menu;